import { useEffect, useRef, useState } from "react";

import { Modal as _Modal, type ModalProps, Popover, useHotkeys, useTimeout } from "~/ui";

export const Modal = (props: ModalProps) => {
  const {
    title,
    children,
    headerClassName,
    contentClassName,
    bodyClassName,
    closeButtonClassName,
    overlayProps,
    withCloseButton,
    closeButtonProps,
    ...rest
  } = props;
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const { start: hideExitConfirmationDelayed } = useTimeout(() => setShowExitConfirmation(false), 3000);
  const [formDirty, setFormDirty] = useState(false);
  const contentRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const handleFormChange = (e: Event) => {
      const target = e.target;
      if (!(target instanceof Node)) {
        return;
      }

      if (contentRef.current?.contains(target)) {
        setFormDirty(true);
      }
    };
    document.addEventListener("scformchange", handleFormChange);
    return () => {
      document.removeEventListener("scformchange", handleFormChange);
    };
  }, []);
  // Callers often close by flipping `opened` from their own Cancel/success paths without calling onClose; clear the
  // dirty flag and exit cue there too so a reopened, untouched form is not treated as having unsaved changes.
  useEffect(() => {
    if (!rest.opened) {
      setFormDirty(false);
      setShowExitConfirmation(false);
    }
  }, [rest.opened]);
  const onClose = () => {
    setFormDirty(false);
    props.onClose();
  };
  const handleEsc = () => {
    if (!formDirty) {
      onClose();
    } else {
      setShowExitConfirmation(true);
      hideExitConfirmationDelayed();
    }
  };
  useHotkeys([["Escape", handleEsc]]);

  return (
    <>
      <_Modal.Root {...rest} onClose={onClose} closeOnClickOutside={false} closeOnEscape={false}>
        <_Modal.Overlay
          {...overlayProps}
          onClick={() => {
            setShowExitConfirmation(true);
            hideExitConfirmationDelayed();
          }}
        />
        <_Modal.Content ref={contentRef} className={contentClassName}>
          <_Modal.Header className={headerClassName}>
            <_Modal.Title>{title}</_Modal.Title>
            {withCloseButton !== false && (
              <Popover opened={showExitConfirmation} position="right" withArrow withinPortal>
                <Popover.Target>
                  {/* The icon-only button needs an accessible name; a caller's own aria-label wins. */}
                  <_Modal.CloseButton aria-label="Close" {...closeButtonProps} className={closeButtonClassName} />
                </Popover.Target>
                <Popover.Dropdown>Click to exit</Popover.Dropdown>
              </Popover>
            )}
          </_Modal.Header>
          <_Modal.Body className={bodyClassName}>{children}</_Modal.Body>
        </_Modal.Content>
      </_Modal.Root>
    </>
  );
};
